import { useRef } from "react";
import { Menu, Divider } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useTranslation } from "@/lib/i18n";
import { MenuItem } from "@/features/admin/components";
import {
  LocationSettingsDialog,
  useDeleteDashboardLocationMutation,
} from "@/features/admin/features/dashboards";
import { ModalComponent, confirm, showToast } from "@/features/shared/components";
import { Location } from "@/lib/api";
import { useAdminDashboard } from "@/features/admin/hooks";

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  location: Location | null;
};

const EditLocationMenu = ({ anchorEl, onClose, location }: Props) => {
  const { t } = useTranslation();
  const deleteDashboardLocationMutation = useDeleteDashboardLocationMutation();

  const dashboard = useAdminDashboard();

  const editLocationModalRef = useRef<ModalComponent>();

  const open = Boolean(anchorEl);

  const handleDelete = async () => {
    const confirmed = await confirm(
      t("admin.dashboard_settings.location_settings.remove_confirm.title"),
      t("admin.dashboard_settings.location_settings.remove_confirm.body"),
    );

    if (confirmed) {
      deleteDashboardLocationMutation.mutate(
        {
          locationId: String(location?.id),
          dashboardId: dashboard.id,
        },
        {
          onSuccess: () => {
            onClose();
            showToast(t("admin.messages.location_deleted"));
          },
        },
      );
    }
  };

  return (
    <>
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        <MenuItem
          icon={<EditIcon fontSize="small" />}
          title={t("admin.dashboard_settings.location_settings.menu.options.edit")}
          onClick={() => editLocationModalRef.current?.open?.()}
        />
        <Divider />
        <MenuItem
          title={t("admin.dashboard_settings.location_settings.menu.options.delete")}
          icon={<DeleteIcon fontSize="small" />}
          onClick={handleDelete}
        />
      </Menu>
      <LocationSettingsDialog
        ref={editLocationModalRef}
        initialValues={location ?? undefined}
        onClose={onClose}
      />
    </>
  );
};

export default EditLocationMenu;
